/* eslint-disable */

import { TUnitType } from '../api/analytics/UnitType';
import { ColumnRangeData, RoundChartData, TreemapData } from '../types/charts/chart-options';
import { DraggableMenuItemData } from '../types/databuilder/databuilder';
import { calculatePercentage, ChartDataResponse } from './chart.helper';

// Calculate z values for variable pie charts
const calculateZValuesForVariablePie = (yValues: number[]): number[] => {
	return yValues.map((y) => (isNaN(y) || !isFinite(y) || y < 0 ? 1 : y));
};

export const getDefaultSingleSeriesData = (
	chartData: object[],
	unitType?: TUnitType,
	selected2ndUnitType?: TUnitType,
	numberOfDecimals?: number,
	droppedFactsCount?: number,
	chartColor?: string,
	grandTotal?: [{ [key: string]: number }],
	showPercentages?: boolean,
	droppedFacts?: DraggableMenuItemData[],
	chartType?: string
) => {
	const response: ChartDataResponse = {
		xAxisArray: [],
		yAxisArray: [],
		valueArray: [],
		valueArray2: [],
		seriesData: [],
		lookupData: [[], []],
	};
	const singleSeriesFinalArray: RoundChartData = { data: [], data2: [] };

	if (chartData) {
		chartData.map((value) => {
			const hasThree = Object.keys(value).length > 2;
			type ObjectKey = keyof typeof value;
			Object.keys(value).map((key: string, index: number) => {
				const prop = key as ObjectKey;

				if (index === 0) {
					response.xAxisArray.push(value[prop] as string);
				}

				const percentage = calculatePercentage(
					(prop as string).replace(/([A-Z])/g, ' $1').trim(),
					grandTotal,
					value[prop] as number | null | undefined
				);

				const total = !showPercentages
					? (value[prop] as string)
					: percentage
					? percentage.toString()
					: '0';

				if (hasThree) {
					if (index === 1) {
						response.valueArray2.push(total);
					} else if (index === 2) {
						response.valueArray.push(total);
					}
				} else {
					if (index === 1) {
						response.valueArray.push(total);
					}
				}
			});
		});
	}

	if (chartData) {
		const numberOfDecimalsSeries1 =
			droppedFacts && droppedFacts?.length > 0
				? droppedFacts[0]?.data?.numDecimals
				: numberOfDecimals;
		const numberOfDecimalsSeries2 =
			droppedFacts && droppedFacts?.length > 1
				? droppedFacts[1]?.data?.numDecimals
				: numberOfDecimals;

		const hasCalculatedFacts =
			droppedFactsCount &&
			droppedFactsCount > 1 &&
			droppedFacts?.some((fact) => fact.data?.calculated);

		const numCalculatedFacts =
			droppedFactsCount &&
			droppedFactsCount > 1 &&
			droppedFacts?.filter((fact) => fact.data?.calculated).length;

		const calcFact = droppedFacts?.find((fact) => fact.data?.calculated);
		const calcFactIdx = droppedFacts?.findIndex((item) => item === calcFact);
		const calcFactIsFirstIdx = calcFactIdx == 0;

		// For variablepie, we need to calculate z values
		const isVariablePie = chartType === 'variablepie';

		chartData.map((_value, index) => {
			//TODO: update so the plotValue var matches up with the valueArray var
			let plotValue = parseFloat(response.valueArray2[index]);
			let plotValue2 = parseFloat(response.valueArray[index]);

			if (hasCalculatedFacts && calcFactIsFirstIdx && numCalculatedFacts == 1) {
				plotValue = parseFloat(response.valueArray[index]);
				plotValue2 = parseFloat(response.valueArray2[index]);
			}

			const keys = Object.keys(_value);
			const categoryValue = response.xAxisArray[index];

			// Grab raw numeric values from original chartData
			const rawValue1 = keys[1] ? Number((_value as any)[keys[1]]) || 0 : 0;
			const rawValue2 = keys[2] ? Number((_value as any)[keys[2]]) || 0 : 0;

			if (!showPercentages) {
				if (unitType?.code === 'PCT') {
					plotValue = plotValue * 100;
				}

				if (
					selected2ndUnitType?.code === 'PCT' ||
					(droppedFactsCount && droppedFactsCount == 1 && unitType?.code === 'PCT')
				) {
					plotValue2 = plotValue2 * 100;
				}
			}

			const decimalValue = parseFloat(plotValue.toFixed(numberOfDecimalsSeries1 ?? 2));

			//for some reason if the arrays dont have the exact same 'y' and 'value' props then it wont reflect in final list
			singleSeriesFinalArray.data.push({
				name: categoryValue,
				y: decimalValue,
				value: rawValue1,
			});
			singleSeriesFinalArray.data2.push({
				name: categoryValue,
				y: parseFloat(
					plotValue2.toFixed(
						isNaN(plotValue)
							? numberOfDecimalsSeries1 ?? 2
							: numberOfDecimalsSeries2 ?? 2
					)
				),
				value: rawValue1,
			});
		});

		// Extract y values from the built data array for z calculation
		const yValuesForZ = isVariablePie
			? singleSeriesFinalArray.data
					.map((item: any) => item.value)
					.filter((y) => !isNaN(y) && isFinite(y) && y >= 0)
			: [];

		// Calculate z values for variablepie if needed
		const zValues =
			isVariablePie && yValuesForZ.length > 0
				? calculateZValuesForVariablePie(yValuesForZ)
				: [];

		// Add z values to data if variablepie
		if (isVariablePie && zValues.length > 0) {
			singleSeriesFinalArray.data.forEach((item, idx) => {
				if (idx < zValues.length) {
					(item as any).z = zValues[idx];
				}
			});
			singleSeriesFinalArray.data2.forEach((item, idx) => {
				if (idx < zValues.length) {
					(item as any).z = zValues[idx];
				}
			});
		}

		const seriesName1 =
			droppedFacts && droppedFacts?.length > 0 ? droppedFacts[0]?.data?.title : 'series 1';

		const seriesName2 =
			droppedFacts && droppedFacts?.length > 0 ? droppedFacts[1]?.data?.title : 'series 2';

		const firstSeriesName = seriesName1;
		const secondSeriesName = seriesName2;

		response.seriesData = [
			{
				name: firstSeriesName,
				data:
					droppedFactsCount && droppedFactsCount > 1
						? singleSeriesFinalArray.data
						: singleSeriesFinalArray.data2 ?? [],
				color: chartColor,
			},
		];

		if (droppedFactsCount && droppedFactsCount > 1) {
			response.seriesData.push({
				name: secondSeriesName,
				data: singleSeriesFinalArray.data2 ?? [],
				type: 'line',
				color: '#ABB8C3',
				yAxis: 1,
				colorAxis: 1,
			});
		}
	}

	return response;
};

export const getOdometerData = (
	chartData: object[],
	unitType?: TUnitType,
	numberOfDecimals?: number,
	grandTotal?: [{ [key: string]: number }],
	showPercentages?: boolean
) => {
	const response: ChartDataResponse = {
		xAxisArray: [],
		yAxisArray: [],
		valueArray: [],
		valueArray2: [],
		seriesData: [],
		lookupData: [[], []],
	};

	if (chartData) {
		chartData.map((value) => {
			type ObjectKey = keyof typeof value;
			Object.keys(value).map((key: string, index: number) => {
				const prop = key as ObjectKey;

				if (index === 0) {
					response.xAxisArray.push(value[prop] as string);
				}
				if (index === 1) {
					const percentage = calculatePercentage(
						(prop as string).replace(/([A-Z])/g, ' $1').trim(),
						grandTotal,
						value[prop] as number | null | undefined
					);

					response.valueArray.push(
						!showPercentages
							? (value[prop] as string)
							: percentage
							? percentage.toString()
							: '0'
					);
				}
			});
		});

		const odometerFinalArray = chartData.map((_value, index) => {
			let plotValue = parseFloat(response.valueArray[index]);

			if (showPercentages || unitType?.code === 'PCT') {
				plotValue = plotValue * 100;
			}

			return parseFloat(plotValue.toFixed(numberOfDecimals));
		});

		response.seriesData = [
			{
				name: response.xAxisArray[0],
				data: odometerFinalArray ?? [],
			},
		];
	}

	return response;
};

export const getColumnRangeData = (
	chartData: object[],
	unitType?: TUnitType,
	numberOfDecimals?: number,
	chartColor?: string,
	grandTotal?: [{ [key: string]: number }],
	showPercentages?: boolean,
	droppedFacts?: DraggableMenuItemData[]
) => {
	const response: ChartDataResponse = {
		xAxisArray: [],
		yAxisArray: [],
		valueArray: [],
		valueArray2: [],
		seriesData: [],
		lookupData: [[], []],
	};
	const singleSeriesFinalArray: ColumnRangeData = { data: [] };

	if (chartData) {
		chartData.map((value) => {
			const hasThree = Object.keys(value).length > 2;
			type ObjectKey = keyof typeof value;
			Object.keys(value).map((key: string, index: number) => {
				const prop = key as ObjectKey;

				if (index === 0) {
					response.xAxisArray.push(value[prop] as string);
				}

				const percentage = calculatePercentage(
					(prop as string).replace(/([A-Z])/g, ' $1').trim(),
					grandTotal,
					value[prop] as number | null | undefined
				);

				const total = !showPercentages
					? (value[prop] as string)
					: percentage
					? percentage.toString()
					: '0';

				if (hasThree) {
					if (index === 1) {
						response.valueArray2.push(total);
					} else if (index === 2) {
						response.valueArray.push(total);
					}
				} else {
					if (index === 1) {
						response.valueArray.push(total);
					}
				}
			});
		});
	}

	if (chartData) {
		const numberOfDecimalsSeries1 =
			droppedFacts && droppedFacts?.length > 0
				? droppedFacts[0]?.data?.numDecimals
				: numberOfDecimals;
		const numberOfDecimalsSeries2 =
			droppedFacts && droppedFacts?.length > 1
				? droppedFacts[1]?.data?.numDecimals
				: numberOfDecimals;

		chartData.map((_value, index) => {
			let plotValue = parseFloat(response.valueArray2[index]);
			let plotValue2 = parseFloat(response.valueArray[index]);

			if (!showPercentages) {
				if (unitType?.code === 'PCT') {
					plotValue = plotValue * 100;
					plotValue2 = plotValue2 * 100;
				}
			}

			const low = plotValue >= plotValue2 ? plotValue2 : plotValue;
			const high = plotValue >= plotValue2 ? plotValue : plotValue2;

			const columnRangeData: [number, number] = [
				parseFloat(low.toFixed(numberOfDecimalsSeries1)),
				parseFloat(
					high.toFixed(
						isNaN(plotValue) ? numberOfDecimalsSeries1 : numberOfDecimalsSeries2
					)
				),
			];

			singleSeriesFinalArray.data.push(columnRangeData);
		});

		response.seriesData = [
			{
				name:
					droppedFacts && droppedFacts?.length > 0
						? droppedFacts[0]?.data?.title
						: 'series 1',
				data: singleSeriesFinalArray.data,
				color: chartColor,
			},
		];
	}

	return response;
};

export const getTreemapSquarifiedData = (
	chartData: object[],
	unitType?: TUnitType,
	numberOfDecimals?: number,
	grandTotal?: [{ [key: string]: number }],
	showPercentages?: boolean,
	droppedFacts?: DraggableMenuItemData[]
) => {
	const response: ChartDataResponse = {
		xAxisArray: [],
		yAxisArray: [],
		valueArray: [],
		valueArray2: [],
		seriesData: [],
		lookupData: [[], []],
	};
	const singleSeriesFinalArray: TreemapData = { data: [] };

	if (chartData) {
		chartData.map((value) => {
			const hasThree = Object.keys(value).length > 2;
			type ObjectKey = keyof typeof value;
			Object.keys(value).map((key: string, index: number) => {
				const prop = key as ObjectKey;

				if (index === 0) {
					response.xAxisArray.push(value[prop] as string);
				}

				const percentage = calculatePercentage(
					(prop as string).replace(/([A-Z])/g, ' $1').trim(),
					grandTotal,
					value[prop] as number | null | undefined
				);

				const total = !showPercentages
					? (value[prop] as string)
					: percentage
					? percentage.toString()
					: '0';

				if (hasThree) {
					if (index === 1) {
						response.valueArray2.push(total);
					} else if (index === 2) {
						response.valueArray.push(total);
					}
				} else {
					if (index === 1) {
						response.valueArray.push(total);
					}
				}
			});
		});
	}

	if (chartData) {
		chartData.map((_value, index) => {
			let plotValue =
				response.valueArray2[index] && response.valueArray2[index].length > 0
					? parseFloat(response.valueArray2[index])
					: parseFloat(response.valueArray[index]);

			const categoryValue = response.xAxisArray[index];
			const keys = Object.keys(_value);

			// Grab raw numeric values from original chartData
			const rawValue1 = keys[1] ? Number((_value as any)[keys[1]]) || 0 : 0;
			const rawValue2 = keys[2] ? Number((_value as any)[keys[2]]) || 0 : 0;

			if (!showPercentages) {
				if (unitType?.code === 'PCT') {
					plotValue = plotValue * 100;
				}
			}

			singleSeriesFinalArray.data.push({
				name: categoryValue,
				value: rawValue1,
				y: parseFloat(plotValue.toFixed(numberOfDecimals)),
			});
		});

		const firstSeriesName =
			droppedFacts && droppedFacts?.length > 0 ? droppedFacts[0]?.data?.title : 'series 1';

		response.seriesData = [
			{
				name: firstSeriesName,
				data: singleSeriesFinalArray.data,
			},
		];
	}

	return response;
};

export const getBulletGraphData = (
	chartData: object[],
	unitType?: TUnitType,
	selected2ndUnitType?: TUnitType,
	numberOfDecimals?: number,
	droppedFactsCount?: number,
	chartColor?: string,
	grandTotal?: [{ [key: string]: number }],
	showPercentages?: boolean,
	droppedFacts?: DraggableMenuItemData[],
	chartType?: string,
	isMultiSeries?: boolean
) => {
	const response: ChartDataResponse = {
		xAxisArray: [],
		yAxisArray: [],
		valueArray: [],
		valueArray2: [],
		seriesData: [],
		lookupData: [[], []],
	};

	if (!chartData) return response;

	const numberOfDecimalsSeries1 =
		droppedFacts && droppedFacts?.length > 0
			? droppedFacts[0]?.data?.numDecimals
			: numberOfDecimals;
	const numberOfDecimalsSeries2 =
		droppedFacts && droppedFacts?.length > 1
			? droppedFacts[1]?.data?.numDecimals
			: numberOfDecimals;

	// Handle multi-series bullet: group data by category and pair series
	if (isMultiSeries && droppedFactsCount && droppedFactsCount > 1) {
		const dataByCategory: { [key: string]: { series: string; value: number }[] } = {};

		chartData.forEach((row) => {
			const keys = Object.keys(row);
			const categoryValue = keys[0] ? (row as any)[keys[0]] : 'Unknown';
			const seriesName = keys[1] ? (row as any)[keys[1]] : 'Unknown';
			let value = keys[2] ? Number((row as any)[keys[2]]) : 0;

			// Apply percentage conversion if needed
			if (!showPercentages) {
				if (seriesName === droppedFacts?.[0]?.data?.title && unitType?.code === 'PCT') {
					value *= 100;
				} else if (
					seriesName === droppedFacts?.[1]?.data?.title &&
					selected2ndUnitType?.code === 'PCT'
				) {
					value *= 100;
				}
			}

			if (!dataByCategory[categoryValue]) {
				dataByCategory[categoryValue] = [];
			}
			dataByCategory[categoryValue].push({
				series: seriesName,
				value: value,
			});
		});

		// Create bullet series combining first and second series values
		Object.keys(dataByCategory).forEach((category) => {
			const seriesData = dataByCategory[category];

			if (seriesData.length >= 2) {
				// Treat first value as y and second as target
				const yValue = parseFloat(
					seriesData[0].value.toFixed(numberOfDecimalsSeries1 ?? 2)
				);
				const targetValue = parseFloat(
					seriesData[1].value.toFixed(numberOfDecimalsSeries2 ?? 2)
				);

				response.xAxisArray.push(category);
				response.valueArray.push(seriesData[0].value.toString());
				response.valueArray2.push(seriesData[1].value.toString());

				if (!response.seriesData[0]) {
					response.seriesData[0] = {
						name:
							droppedFacts && droppedFacts?.length > 0
								? droppedFacts[0]?.data?.title
								: 'Series 1',
						type: 'bullet',
						color: chartColor ?? '#8ed1fc',
						data: [],
						dataLabels: { enabled: true },
					};
				}

				(response.seriesData[0].data as any[]).push({
					name: category,
					y: yValue,
					target: targetValue,
				});
			} else if (seriesData.length === 1) {
				// Single value, no target
				const yValue = parseFloat(
					seriesData[0].value.toFixed(numberOfDecimalsSeries1 ?? 2)
				);

				response.xAxisArray.push(category);
				response.valueArray.push(seriesData[0].value.toString());

				if (!response.seriesData[0]) {
					response.seriesData[0] = {
						name:
							droppedFacts && droppedFacts?.length > 0
								? droppedFacts[0]?.data?.title
								: 'Series 1',
						type: 'bullet',
						color: chartColor ?? '#8ed1fc',
						data: [],
						dataLabels: { enabled: true },
					};
				}

				(response.seriesData[0].data as any[]).push({
					name: category,
					y: yValue,
				});
			}
		});
	} else {
		// Single series bullet chart (original behavior)
		const singleSeriesFinalArray: { data: any[] } = {
			data: [],
		};

		// Collect all values to process
		chartData.forEach((row, index) => {
			const keys = Object.keys(row);
			const categoryValue = keys[0] ? (row as any)[keys[0]] : `Category ${index}`;

			// Main value
			let yValue = keys[1] ? Number((row as any)[keys[1]]) : 0;
			// Target value (optional)
			let targetValue = keys[2] ? Number((row as any)[keys[2]]) : undefined;

			// Convert PCT if needed
			if (!showPercentages) {
				if (unitType?.code === 'PCT') yValue *= 100;
				if (selected2ndUnitType?.code === 'PCT')
					targetValue = targetValue ? targetValue * 100 : undefined;
			}

			// Add to xAxisArray for proper rendering
			response.xAxisArray.push(categoryValue);
			response.valueArray.push(yValue.toString());
			if (targetValue !== undefined) {
				response.valueArray2.push(targetValue.toString());
			}

			singleSeriesFinalArray.data.push({
				name: categoryValue,
				y: parseFloat(yValue.toFixed(numberOfDecimalsSeries1 ?? 2)),
				target:
					targetValue !== undefined
						? parseFloat(targetValue.toFixed(numberOfDecimalsSeries2 ?? 2))
						: undefined,
			});
		});

		// For bullet charts, all data goes in a single series
		response.seriesData = [
			{
				name:
					droppedFacts && droppedFacts?.length > 0
						? droppedFacts[0]?.data?.title
						: 'Series 1',
				type: 'bullet',
				color: chartColor ?? '#8ed1fc',
				data: singleSeriesFinalArray.data,
				dataLabels: { enabled: true },
			},
		];
	}

	return response;
};
